import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Check,
  ChevronLeft,
  ImageIcon,
  Minus,
  PackageCheck,
  Plus,
  ShieldCheck,
  ShoppingBasket,
  Sparkles,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import SEOHead from "../components/SEOHead";
import PublicBreadcrumbs from "../components/PublicBreadcrumbs";
import MediaPreviewModal from "../components/MediaPreviewModal";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import { useApp } from "../context/AppContext";
import { productsApi } from "../lib/api";
import { formatRupiah } from "../lib/format";
import { getMediaType } from "../lib/media";
import { stockBadge } from "../lib/storage";
import {
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildProductSchema,
  buildWebsiteSchema,
} from "../lib/seo";

const normalizeProduct = (product) => {
  const categoryName =
    typeof product?.category === "string"
      ? product.category
      : product?.category?.name || product?.category_name || "Produk Herbal";

  return {
    ...product,
    category: categoryName,
    category_id: product?.category_id || product?.category?.id,
    benefit:
      product?.benefit ||
      product?.benefits ||
      product?.short_description ||
      product?.description ||
      "",
    benefits: product?.benefits || product?.benefit || "",
    short_description: product?.short_description || product?.description || "",
    active: product?.active ?? product?.is_active ?? true,
    is_active: product?.is_active ?? product?.active ?? true,
    gallery: Array.isArray(product?.gallery) ? product.gallery : [],
  };
};

const uniqueImages = (product) => {
  const images = [product?.image, ...(product?.gallery || [])]
    .map((image) => (typeof image === "string" ? image.trim() : ""))
    .filter(Boolean);

  return Array.from(new Set(images));
};

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { products, cart, addToCart, setCartQty, openCart } = useApp();
  const cachedProduct = products.find((item) => item.slug === slug);
  const [product, setProduct] = useState(cachedProduct ? normalizeProduct(cachedProduct) : null);
  const [loading, setLoading] = useState(!cachedProduct);
  const [selectedImage, setSelectedImage] = useState(cachedProduct?.image || "");
  const [previewMedia, setPreviewMedia] = useState(null);

  useEffect(() => {
    let active = true;

    const loadProduct = async () => {
      try {
        setLoading(true);
        const data = await productsApi.getBySlug(slug);
        if (!active) return;
        const normalized = normalizeProduct(data);
        setProduct(normalized);
        setSelectedImage(normalized.image || uniqueImages(normalized)[0] || "");
      } catch (error) {
        if (active && !cachedProduct) {
          toast.error(error.message || "Produk tidak ditemukan");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (slug) {
      loadProduct();
    }

    return () => {
      active = false;
    };
  }, [cachedProduct, slug]);

  const gallery = useMemo(() => uniqueImages(product), [product]);
  const badge = stockBadge(product?.stock || 0);
  const soldOut = !product || Number(product.stock || 0) <= 0;
  const inCart = product ? cart.find((item) => item.productId === product.id) : null;
  const qty = inCart?.quantity || 0;
  const hasComparePrice =
    product?.compare_price && Number(product.compare_price) > Number(product.price);
  const discountPercent = hasComparePrice
    ? Math.round(((Number(product.compare_price) - Number(product.price)) / Number(product.compare_price)) * 100)
    : 0;

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return products
      .filter((item) => {
        const normalized = normalizeProduct(item);
        return (
          normalized.id !== product.id &&
          normalized.category === product.category &&
          (normalized.is_active ?? normalized.active ?? true) !== false
        );
      })
      .slice(0, 4)
      .map(normalizeProduct);
  }, [product, products]);

  const breadcrumbItems = product
    ? [
        { name: "Beranda", path: "/" },
        { name: product.category, path: `/#cat-${product.category.toLowerCase().replace(/\s+/g, "-")}` },
        { name: product.name, path: `/produk/${product.slug}` },
      ]
    : [
        { name: "Beranda", path: "/" },
        { name: "Detail Produk", path: `/produk/${slug}` },
      ];

  const schema = product
    ? [
        buildOrganizationSchema(),
        buildWebsiteSchema(),
        buildBreadcrumbSchema(breadcrumbItems),
        buildProductSchema(product, {
          path: `/produk/${product.slug}`,
          image: product.image,
          description: product.short_description || product.description || product.benefit,
        }),
      ]
    : [];

  const addProductToCart = () => {
    if (!product) return;
    if (qty >= product.stock) {
      toast.warning("Jumlah sudah mencapai batas stok");
      return;
    }
    addToCart(product.id, 1);
    toast.success(`${product.name} ditambahkan ke keranjang`);
  };

  const increaseQty = () => {
    if (!product) return;
    if (qty >= product.stock) {
      toast.warning("Stok tidak mencukupi");
      return;
    }
    setCartQty(product.id, qty + 1);
  };

  const decreaseQty = () => {
    if (!product) return;
    setCartQty(product.id, qty - 1);
  };

  if (loading && !product) {
    return (
      <main className="min-h-[70vh] bg-stone-50 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="aspect-square animate-pulse rounded-[2rem] bg-stone-200" />
            <div className="space-y-5">
              <div className="h-8 w-40 animate-pulse rounded-full bg-stone-200" />
              <div className="h-12 w-3/4 animate-pulse rounded-xl bg-stone-200" />
              <div className="h-24 animate-pulse rounded-2xl bg-stone-200" />
              <div className="h-14 w-48 animate-pulse rounded-xl bg-stone-200" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="min-h-[70vh] bg-stone-50 px-4 py-16 sm:px-6 lg:px-8">
        <SEOHead
          title="Produk Tidak Ditemukan"
          description="Produk Teras Alami yang Anda buka tidak ditemukan atau sedang tidak tersedia."
          path={`/produk/${slug}`}
          noIndex
        />
        <div className="mx-auto max-w-2xl rounded-[2rem] border border-stone-200 bg-white p-8 text-center shadow-sm">
          <PackageCheck className="mx-auto h-12 w-12 text-stone-400" />
          <h1 className="mt-4 font-display text-3xl font-semibold text-stone-900">
            Produk tidak ditemukan
          </h1>
          <p className="mt-3 text-stone-600">
            Produk mungkin sedang dinonaktifkan atau URL yang dibuka sudah berubah.
          </p>
          <Button asChild className="mt-6 rounded-xl bg-emerald-600 hover:bg-emerald-700">
            <Link to="/">Kembali ke katalog</Link>
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-stone-50 pb-28 md:pb-0">
      <SEOHead
        title={product.name}
        description={product.short_description || product.description || product.benefit}
        path={`/produk/${product.slug}`}
        type="product"
        image={product.image}
        schema={schema}
      />

      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <PublicBreadcrumbs items={breadcrumbItems} />
          <Link
            to="/#produk"
            className="inline-flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-emerald-700"
          >
            <ChevronLeft className="h-4 w-4" />
            Kembali ke katalog
          </Link>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(380px,0.95fr)] lg:px-8 lg:py-12">
        <div className="space-y-4">
          <div className="overflow-hidden rounded-[2rem] border border-stone-200 bg-white shadow-sm">
            <div className="relative aspect-square bg-stone-100">
              {selectedImage ? (
                <button
                  type="button"
                  onClick={() =>
                    setPreviewMedia({
                      url: selectedImage,
                      title: product.name,
                      type: getMediaType(selectedImage),
                    })
                  }
                  className="block h-full w-full cursor-zoom-in"
                  aria-label={`Preview ${product.name}`}
                >
                  {getMediaType(selectedImage) === "video" ? (
                    <video
                      src={selectedImage}
                      className="h-full w-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={selectedImage}
                      alt={`${product.name} Teras Alami`}
                      className="h-full w-full object-cover"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                    />
                  )}
                </button>
              ) : (
                <div className="flex h-full items-center justify-center text-stone-400">
                  <ImageIcon className="h-16 w-16" />
                </div>
              )}
              {hasComparePrice && (
                <span className="absolute left-4 top-4 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm">
                  Hemat {discountPercent}%
                </span>
              )}
            </div>
          </div>

          {gallery.length > 1 && (
            <div className="grid grid-cols-5 gap-3 sm:grid-cols-6 lg:grid-cols-7">
              {gallery.map((image, index) => (
                <button
                  key={`${image}-${index}`}
                  type="button"
                  onClick={() => setSelectedImage(image)}
                  onDoubleClick={() =>
                    setPreviewMedia({
                      url: image,
                      title: `${product.name} ${index + 1}`,
                      type: getMediaType(image),
                    })
                  }
                  className={`aspect-square overflow-hidden rounded-2xl border bg-white transition ${
                    selectedImage === image
                      ? "border-emerald-500 ring-2 ring-emerald-100"
                      : "border-stone-200 hover:border-emerald-300"
                  }`}
                  aria-label={`Lihat gambar produk ${index + 1}`}
                >
                  {getMediaType(image) === "video" ? (
                    <video src={image} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                  ) : (
                    <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm sm:p-7">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                {product.category}
              </Badge>
              <span className="rounded-full border border-stone-200 px-3 py-1 text-xs font-medium text-stone-600">
                {badge.label}
              </span>
              {product.is_featured && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  Rekomendasi
                </span>
              )}
            </div>

            <h1 className="mt-4 font-display text-3xl font-semibold leading-tight text-stone-950 sm:text-4xl">
              {product.name}
            </h1>
            <p className="mt-4 text-base leading-7 text-stone-600">
              {product.short_description || product.benefit || product.description}
            </p>

            <div className="mt-6 rounded-2xl border border-stone-200 bg-stone-50 p-5">
              {hasComparePrice && (
                <div className="mb-2 flex items-center gap-3">
                  <span className="text-lg text-stone-400 line-through">
                    {formatRupiah(product.compare_price)}
                  </span>
                  <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700">
                    Diskon {discountPercent}%
                  </span>
                </div>
              )}
              <p className="font-display text-3xl font-semibold text-emerald-700">
                {formatRupiah(product.price)}
              </p>
              <p className="mt-2 text-sm text-stone-500">
                Stok tersedia: <span className="font-semibold text-stone-800">{product.stock}</span> pcs
              </p>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-stone-200 p-4">
                <Truck className="h-5 w-5 text-emerald-700" />
                <p className="mt-2 text-sm font-medium text-stone-900">Siap kirim</p>
              </div>
              <div className="rounded-2xl border border-stone-200 p-4">
                <ShieldCheck className="h-5 w-5 text-emerald-700" />
                <p className="mt-2 text-sm font-medium text-stone-900">Herbal aman</p>
              </div>
              <div className="rounded-2xl border border-stone-200 p-4">
                <PackageCheck className="h-5 w-5 text-emerald-700" />
                <p className="mt-2 text-sm font-medium text-stone-900">{product.weight || 0} gram</p>
              </div>
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              {inCart ? (
                <div className="flex h-14 items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 p-1 sm:w-44">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={decreaseQty}
                    className="h-12 w-12 rounded-xl text-emerald-700 hover:bg-emerald-100"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="font-semibold text-emerald-800">{qty}</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={increaseQty}
                    className="h-12 w-12 rounded-xl text-emerald-700 hover:bg-emerald-100"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  onClick={addProductToCart}
                  disabled={soldOut}
                  className="h-14 flex-1 rounded-2xl bg-emerald-600 text-base text-white hover:bg-emerald-700 disabled:bg-stone-200 disabled:text-stone-400"
                >
                  <ShoppingBasket className="mr-2 h-5 w-5" />
                  {soldOut ? "Stok habis" : "Tambah ke keranjang"}
                </Button>
              )}

              <Button
                type="button"
                variant="outline"
                onClick={openCart}
                className="h-14 rounded-2xl border-stone-200 px-6 text-base"
              >
                <Check className="mr-2 h-5 w-5" />
                Lihat keranjang
              </Button>
            </div>
          </div>
        </aside>
      </section>

      <section className="mx-auto grid max-w-7xl gap-6 px-4 pb-10 sm:px-6 lg:grid-cols-3 lg:px-8">
        <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm lg:col-span-2">
          <h2 className="font-display text-2xl font-semibold text-stone-900">Detail produk</h2>
          <div className="mt-4 space-y-4 text-sm leading-7 text-stone-600 sm:text-base">
            <p>{product.description || product.short_description || product.benefit}</p>
            {product.benefits && (
              <div>
                <h3 className="font-semibold text-stone-900">Manfaat utama</h3>
                <p className="mt-2 whitespace-pre-line">{product.benefits}</p>
              </div>
            )}
            {product.how_to_use && (
              <div>
                <h3 className="font-semibold text-stone-900">Cara penggunaan</h3>
                <p className="mt-2 whitespace-pre-line">{product.how_to_use}</p>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50 p-6 shadow-sm">
          <h2 className="font-display text-xl font-semibold text-emerald-950">Catatan konsumsi</h2>
          <p className="mt-3 text-sm leading-7 text-emerald-900">
            Produk herbal Teras Alami ditujukan sebagai pendamping gaya hidup sehat. Konsumsi secara
            wajar dan konsultasikan dengan tenaga kesehatan untuk kondisi medis tertentu.
          </p>
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-2xl font-semibold text-stone-900">
                Produk terkait
              </h2>
              <p className="mt-1 text-stone-600">Pilihan lain dari kategori {product.category}.</p>
            </div>
            <Link to="/#produk" className="hidden text-sm font-medium text-emerald-700 hover:text-emerald-800 sm:block">
              Lihat semua
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {relatedProducts.map((item) => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>
        </section>
      )}
      {previewMedia && (
        <MediaPreviewModal media={previewMedia} onClose={() => setPreviewMedia(null)} />
      )}
    </main>
  );
};

export default ProductDetailPage;
